export const createMarkup = (markup: string) => {
  return { __html: markup };
};

export const getInitials = (name: string) => {
  const nameParts = name
    .trim()
    .replace(/ +\(.+\)/, '') // remove stuff like "(Removed)"
    .split(' ');
  let initials = name;
  if (nameParts.length < 2) {
    initials = nameParts[0].charAt(0);
  } else {
    initials = `${nameParts[0].charAt(0)} ${nameParts[1].charAt(0)}`;
  }

  return initials;
};

export const isValidEmail = (email: string) => {
  // https://www.regular-expressions.info/email.html
  const emailRegex = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/, 'i');
  return emailRegex.test(email);
};

export const capitalizeFirstLetter = (val: any): string => {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
};

export const snakeCaseToPascalCase = (snake: string) => {
  return snake
    .split('_')
    .filter((word) => word.length)
    .map((word) => capitalizeFirstLetter(word))
    .join('');
};

/**
 * A registry name rendered as a person writes it. NPPES stores names in capitals, so a provider's
 * own account was created as "JOEL CHRISTOPHER OWENS" and that string went on to be their signer
 * name (QA v26.10, #8).
 *
 * Deliberately naive about particles: "MCDONALD" comes back "Mcdonald" and "VAN DER BERG" keeps
 * its lowercase-in-the-middle convention only if the registry had it. Guessing at Mc/Mac/van/de
 * gets real names wrong in both directions, and this only ever prefills a field the person is
 * looking at and can correct.
 */
export const toDisplayPersonName = (name: string): string =>
  name
    .toLocaleLowerCase()
    .replace(
      /(^|[\s'’-])([a-zà-ÿ])/g,
      (_match, boundary, letter) => `${boundary}${letter.toLocaleUpperCase()}`
    );

/**
 * Legal-entity suffixes and clinical credentials that are upper case because that is how they are
 * written, not because the registry shouts. Title-casing these produces "Llc" and "Msn".
 */
const PRESERVED_UPPERCASE = new Set([
  'LLC',
  'PLLC',
  'PC',
  'PA',
  'LLP',
  'LP',
  'INC',
  'LTD',
  'MD',
  'DO',
  'DDS',
  'DMD',
  'NP',
  'FNP',
  'DNP',
  'APN',
  'APRN',
  'MSN',
  'RN',
  'PT',
  'OT',
  'PHD',
  'II',
  'III',
  'IV'
]);

/**
 * The NPPES name as a person would write it, for the legal entity field.
 *
 * The registry answers in block capitals — "ERIC CARON, MSN, APN" — and that string prefilled the
 * legal entity name and flowed into the agreements unchanged (Charles, 2026-09-14). A contract
 * naming its counterparty in shouted caps with a credential suffix reads as machine output, which
 * is not what you want on the one document a provider might have to rely on.
 *
 * NPPES separates credentials with a comma, so everything after the first one is kept exactly as
 * given: they are abbreviations, not words. Within the name itself, tokens that are upper case
 * because that is how they are written — LLC, PC, MD — survive too.
 *
 * Only ever a prefill. The field sits in front of the person with an explicit note to change it,
 * which is the right place to correct anything this gets wrong.
 */
export const toDisplayEntityName = (name: string): string => {
  const [primary, ...credentials] = name.split(',');
  const titled = primary
    .trim()
    .split(/\s+/)
    .map((word) => {
      const isWrittenUpperCase =
        PRESERVED_UPPERCASE.has(word.toLocaleUpperCase()) && word === word.toLocaleUpperCase();
      return isWrittenUpperCase ? word : toDisplayPersonName(word);
    })
    .join(' ');
  return [titled, ...credentials.map((part) => part.trim())].filter(Boolean).join(', ');
};
